/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {BigWinConstants} from '@gongxigames/framework/slot/modules/bigwin/BigWinConstants';

import {BLEND_MODES, filters, Sprite, Text, Texture} from 'pixi.js';
import {
    Easing, EmitterV5,
    Meter, MovieClip,
    Spine, Store,
    Timer, Tween,
    UpdateManager,
} from '@gongxigames/framework/core';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {Container} from 'pixi.js';
import {$Container, $Spine} from '@gongxigames/framework/core/fragments/BuilderY';
import {$FullScreenFader} from '@gongxigames/framework/slotV2/JSX/FullScreenFader';
import {$FullScreenFit} from '@gongxigames/framework/slotV2/JSX/FullScreenFit';
import {bigWinStore} from '@gongxigames/framework/slotV2/modules/bigwin/bigWinStore';
import {$BigWinParticles} from '@gongxigames/framework/slotV2/modules/bigwin/bigWinParticles';

type BigWinViewV2Options = {
    onBigWinFinished: () => void;
    onMeterStarted: () => void;
    onMeterFinalValue: () => void;
    onMeterFinished: () => void;
};

export class BigWinViewV2 {
    public static readonly NAME: symbol = Symbol('BigWinViewV2');

    public container!: Container;

    protected _spineContainer!: Sprite;
    protected _particlesContainer!: Sprite;
    protected _particles!: Container;
    protected _spine!: Spine;

    protected showPopup = Store.signal(false);

    protected _emitter?: EmitterV5;
    protected _finalEmitter?: EmitterV5;

    protected _BWType!: string;

    protected _updater: UpdateManager = new UpdateManager(true);

    protected _meter?: Meter;
    protected _from!: number;
    protected _to!: number;

    protected _skipTimer?: Timer;
    protected _darkTimer?: Timer;
    protected _animationTimer?: Timer;
    protected _particlesTimer?: Timer;
    protected _finalDelayTimer?: Timer;

    protected _canSkip = false;
    protected _isSkippableImmediately = false;
    protected _isPlayWinMeter = false;
    protected _isPlaying = false;
    protected _playingSound!: string;

    constructor(protected options: BigWinViewV2Options) {
        this.init();
    }

    public init(): void {
        slotStore.updateManager.value!.add(this._updater);

        this.createMainScene();
        this.initEvents();
    }

    protected createMainScene(): void {
        const {big_win_width, big_win_height} = bigWinStore;
        const xOffset = Store.computed(() => big_win_width.value / 2);
        const yOffset = Store.computed(() => big_win_height.value / 2);

        this._spineContainer = new Sprite();

        this.container = <$Container name='bigWin' visible={this.showPopup}>
            <$FullScreenFader opacity={0.5} show={true}/>
            <$FullScreenFit width={big_win_width} height={big_win_height}>
                <$Container>
                    <$Container name='bigWin_spineContainer' x={xOffset} y={yOffset}>
                        {this._spineContainer}
                    </$Container>
                </$Container>
            </$FullScreenFit>
        </$Container> as Container;
    }

    public onResourcesLoaded(): void {
        this.initParticles();
        this.initSpineAnimation();
        this.addMaskOnRays();
        this.addParticlesEmitterToSlot();
        this.showPopup.value = false;
    }

    public play(type: string, isSkippableImmediately: boolean, from: number, to: number, isPlayWinMeter: boolean): void {
        this._BWType = type;
        this._isSkippableImmediately = isSkippableImmediately;
        this._isPlayWinMeter = isPlayWinMeter;
        this._from = from;
        this._to = to;
        this._isPlaying = true;

        this.setSkipOptions();
        this.playBWAnimation();
        this.startEmitParticles();

        this.container.interactive = true;
        this.container.alpha = 1;
        this.showPopup.value = true;
    }

    public skip(): void {
        if (this._isSkippableImmediately) {
            this.stop();
        } else if (this._canSkip) {
            this.setBurstVisibility(false);
            this.setEndSound();
            this.setCounterToFinalValue();
        } else {
            return;
        }
    }

    protected setEndSound(): void {
        slotStore.soundPlayer.value!.stop(this._playingSound);
        this._playingSound = BigWinConstants.END_SOUND;
        slotStore.soundPlayer.value!.play(this._playingSound);

    }

    protected fade(): void {
        this._updater.add(new Tween(this.container)
            .to({alpha: 0}, BigWinConstants.BIG_WIN_TIMINGS.final_fade)
            .onComplete(() => this._isPlaying && this.stop())
            .start()
        );
    }

    protected stop(): void {
        this.container.interactive = false;

        this._isPlaying = false;
        this._canSkip = false;

        slotStore.soundPlayer.value!.stop(this._playingSound);

        this.clearTimers();
        this.stopEmitters();

        if (this._meter) {
            this._meter.onCompleteCallback = undefined;
            this._meter.stop();
        }

        this._isPlayWinMeter && this.options.onMeterFinished();

        this.setBurstVisibility(true);

        this._spine.state.clearTracks();
        this._spine.skeleton.setToSetupPose();
        this._spineContainer.visible = false;

        this.options.onBigWinFinished();

        this.showPopup.value = false;
    }

    protected initEvents(): void {
        this.container.on('click', () => this.skip());
        this.container.on('tap', () => this.skip());
    }

    /**
     * setup container with particular particles here
     * @protected
     */
    protected initParticles(): void {
        this._particles = <$BigWinParticles /> as Container;
        this._particles.visible = false;

        this._spineContainer.addChild(this._particles);
    }

    protected initSpineAnimation(): void {
        this._spine = <$Spine spine={BigWinConstants.ANIMATION_NAME} play /> as Spine;
        this._spine.start();

        this._spineContainer.addChild(this._spine);
        this._spineContainer.visible = false;
    }

    protected setSkipOptions(): void {
        this._skipTimer = new Timer(() => this._canSkip = true, BigWinConstants.BIG_WIN_TIMINGS.skip_delay, true);
        this._updater.add(this._skipTimer);
    }

    protected playBWAnimation(): void {
        this._animationTimer = new Timer(() => {
            this.playSpineAnimation();
            this.startCounter();
        }, BigWinConstants.BIG_WIN_TIMINGS.animation_delay, true);
        this._updater.add(this._animationTimer);
    }

    protected startEmitParticles(): void {
        this._particlesTimer = new Timer(() => {
            this.emitParticles();
        }, BigWinConstants.BIG_WIN_TIMINGS.particles_delay, true);
        this._updater.add(this._particlesTimer);
    }

    protected startCounter(): void {
        const duration = BigWinConstants.BIG_WIN_TIMINGS.duration - BigWinConstants.BIG_WIN_TIMINGS.animation_delay;
        const counter = this._spine.skeleton.findSlot('bw_0');
        const counterText = new Text('', {
            fontFamily: 'PF DinDisplay Pro',
            fontSize: '395px',
            fontWeight: 'bold',
            fill: [
                '#f48e0c',
                '#fff88b',
                '#f9ffd5',
                '#fff88b',
                '#ffcb2a',
                '#ffcb2a',
                '#f48e0c'
            ],
            stroke: '#810000',
            strokeThickness: 30
        });

        counter.currentSprite.scale.set(1, -1);
        counter.currentSprite.texture = Texture.EMPTY;
        counter.currentSprite.removeChildren();
        counter.currentSprite.addChild(counterText);

        this._meter = new Meter(counterText, Easing.Linear.None, slotStore.currencyFormatter.value!);
        this._meter.from(0).to(this._to - this._from, duration);
        this._meter.start();
        this._meter.onCompleteCallback = this.onCounterFinished.bind(this);
        this._updater.add(this._meter);
        this._meter.updateCallback = () => {
            counterText.x = -counterText.getLocalBounds(counterText.parent.getBounds()).width / 2;
            counterText.y = -counterText.getLocalBounds(counterText.parent.getBounds()).height / 2;
        };

        // start flow meter
        if (this._isPlayWinMeter) {
            this.options.onMeterStarted();
        }
    }

    protected onCounterFinished(): void {
        if (this._meter) {
            this._meter.onCompleteCallback = undefined;
            this._meter.stop();
        }
        this.options.onMeterFinished();

        this.onBigWinAnimationCompleted();
    }

    protected onBigWinAnimationCompleted(): void {
        this._isSkippableImmediately = true;

        this.stopEmitParticles();
        this._finalDelayTimer = new Timer(() => this.fade(), BigWinConstants.BIG_WIN_TIMINGS.final_delay, true);
        this._updater.add(this._finalDelayTimer);
    }

    protected addMaskOnRays(): void {
        const rays = this._spine.skeleton.findSlot('rays2');

        rays.currentSprite.mask = this._spine.skeleton.findSlot('rays_mask').currentSprite;

        if (rays.blendMode === BLEND_MODES.ADD) {
            const addFilter = new filters.AlphaFilter();

            addFilter.blendMode = BLEND_MODES.ADD;
            rays.currentSprite.filters = [addFilter];
            rays.currentSprite.blendMode = BLEND_MODES.NORMAL;
        }
    }

    protected addParticlesEmitterToSlot(): void {
        const slotSprite = this._spine.skeleton.findSlot('emitter').currentSprite;
        const sprite = new Sprite();

        sprite.anchor.set(0.5);

        slotSprite.texture = Texture.EMPTY;
        slotSprite.addChild(sprite);

        this._particlesContainer = sprite;
    }

    protected playSpineAnimation(): void {
        const startAnimationName = slotStore.spineUtils.value!.getAnimationNameByQuality(this._spine, `${this._BWType}_start`);
        const loopAnimationName = slotStore.spineUtils.value!.getAnimationNameByQuality(this._spine, `${this._BWType}_loop`);

        this._spineContainer.visible = true;

        this._spine.state.setAnimation(0, startAnimationName, false);
        this._spine.state.addAnimation(1, loopAnimationName, true, 0);
        this._spine.update(0);
    }

    protected setCounterToFinalValue(): void {
        this._meter?.snapToFinalValue();
        this.options.onMeterFinalValue();
        this.onCounterFinished();
    }

    protected stopEmitParticles(): void {
        if (this._emitter) {
            this._emitter.stopEmit();
        }
        this.emitFinalBurst();
    }

    protected emitParticles(): void {
        const textures = this.getParticlesTextures();

        // NOTE: particles 5 ver, contains textures inside config:
        const config = BigWinConstants.PARTICLES_CONFIG(textures);

        this._emitter = new EmitterV5(
            this._particlesContainer,
            config
        );

        this._updater.add(this._emitter);
        this._emitter.startEmit();
    }

    protected emitFinalBurst(): void {
        const textures = this.getParticlesTextures();
        const config = BigWinConstants.PARTICLES_CONFIG(textures);

        config.frequency = 90;
        config.emitterLifetime = 100;
        config.particlesPerWave = 100;

        this._finalEmitter = new EmitterV5(
            this._particlesContainer,
            config
        );

        this._updater.add(this._finalEmitter);
        this._finalEmitter.startEmit();
    }

    protected getParticlesTextures(): Texture[][] {
        const particlesTextures: Texture[][] = [];

        for (const item of this._particles.children) {
            if (this.needAddParticleType(item as MovieClip)) {
                const particleObjects = (item as MovieClip).frames as Sprite[];

                const textures: Texture[] = particleObjects.map(sprite => sprite.texture);

                particlesTextures.push(textures);
            }
        }

        return particlesTextures;
    }

    protected getParticleObjects(): Sprite[][] {
        const particles: Sprite[][] = [];

        for (const item of this._particles.children) {
            if (this.needAddParticleType(item as MovieClip)) {
                const particleObjects = [...(item as MovieClip).frames] as Sprite[];

                particles.push(particleObjects, particleObjects.slice().reverse());
            }
        }

        return particles;
    }

    protected needAddParticleType(item: MovieClip): boolean {
        return BigWinConstants.PARTICLE_TYPES[this._BWType].indexOf(item.name) >= 0;
    }

    protected stopEmitters(): void {
        this._emitter && this._emitter.stopEmitAndCleanup();
        this._finalEmitter && this._finalEmitter.stopEmitAndCleanup();
    }

    protected clearTimers(): void {
        this._skipTimer && this._skipTimer.stop();
        this._darkTimer && this._darkTimer.stop();
        this._animationTimer && this._animationTimer.stop();
        this._particlesTimer && this._particlesTimer.stop();
        this._finalDelayTimer && this._finalDelayTimer.stop();
    }

    protected setBurstVisibility(value: boolean): void {
        this._spine.skeleton.findSlot('burst').currentSprite.visible = value;
    }
}
